import { observer } from "mobx-react-lite";
import { useMst } from "../../stores/main";
import { frameToPixel } from "./utils/timelineHelpers";
import { useSceneTrimState } from "./hooks/useSceneTrimState";

interface TimelineWorkAreaOverlayProps {
  totalFrames: number;
}

/**
 * Renders overlay regions for time outside the composition boundaries.
 * This provides visual distinction between active scene time and non-working areas.
 * Also shows trimmed end region if scene trimming is active.
 */
export const TimelineWorkAreaOverlay = observer(
  ({ totalFrames }: TimelineWorkAreaOverlayProps) => {
    const { timelineStore } = useMst();
    const { zoom, scrollOffset } = timelineStore;
    const { currentDuration, baselineDuration } = useSceneTrimState();

    // Calculate positions for the overlay regions
    // Left overlay: before frame 0 (when scrolled right)
    const sceneStartX = frameToPixel(0, zoom, scrollOffset);
    // Right overlay: after the last frame
    const sceneEndX = frameToPixel(
      currentDuration || totalFrames,
      zoom,
      scrollOffset
    );

    const baselineFrames = baselineDuration || totalFrames;
    // Calculate trimmed end position if trimming is active
    const trimEndX =
      currentDuration < baselineFrames
        ? frameToPixel(currentDuration, zoom, scrollOffset)
        : null;
    // Only show trimmed region when actually trimmed (not extended)
    const hasTrimming = currentDuration < baselineFrames;

    // Only show left overlay if scene start is visible (scrolled past it)
    const showLeftOverlay = sceneStartX > 0;
    // Right overlay extends from scene end to the right edge of the container
    const showRightOverlay = sceneEndX >= 0;

    return (
      <div className="timeline-work-area-overlay">
        {showLeftOverlay && (
          <div
            className="timeline-work-area-overlay__inactive timeline-work-area-overlay__inactive--left"
            style={{
              left: 0,
              width: sceneStartX,
            }}
          />
        )}
        {/* Trimmed end region (from trimEnd to totalFrames) */}
        {hasTrimming && trimEndX !== null && (
          <div
            className="timeline-work-area-overlay__trimmed-region"
            style={{
              left: Math.max(0, trimEndX),
              width: Math.max(
                0,
                frameToPixel(baselineFrames, zoom, scrollOffset) - trimEndX
              ),
            }}
          />
        )}
        {showRightOverlay && (
          <div
            className="timeline-work-area-overlay__inactive timeline-work-area-overlay__inactive--right"
            style={{
              left: Math.max(0, sceneEndX),
              right: 0,
            }}
          />
        )}
      </div>
    );
  }
);
